import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { FaSearchMinus } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import type { U2000TableColumn } from "../../basic/U2000BasicTableList";
import type { U2000Condition, U2000ConditionType } from "../../basic/U2000BasicSearch";
import U2000BasicSearchFieldCriteriaLine, {
  type U2000BasicSearchFieldCriteriaLineHandle,
  type U2000DraftCriteriaFlushResult,
} from "./U2000BasicSearchFieldCriteriaLine";
import type { U2000SearchStateHandlers } from "../../../../reducer/formReducer/U2000SearchStateHandlers";
import type { CriteriaDynamicDate } from "../../../../reducer/formReducer/U2000SearchState";
import { u2000CoerceLookupCriteriaCondition } from "../../../../tools/u2000SearchCriteriaFieldConditions";
import { resolveCriteriaDisplayValue } from "./U2000SearchPresenceCriteriaDisplay";

export type U2000BasicSearchCriteriaHandle = U2000BasicSearchFieldCriteriaLineHandle;

interface U2000BasicSearchCriteriaProps {
  searchStateHandlers: U2000SearchStateHandlers;
  filterKey?: string | number;
  conditionTypes: U2000ConditionType[];
}

type U2000CriteriaItem = {
  id: string;
  field: U2000TableColumn;
  condition: U2000Condition;
  value: string;
  displayValue?: string;
  dynamicDate?: CriteriaDynamicDate;
  timestamps: number;
};

// Composant enfant pour l'affichage d'une ligne de critère (affiche le champ, la condition et la valeur, avec boutons d'édition et suppression)
const CriteriaLine: React.FC<{
  item: U2000CriteriaItem;
  onEdit: (name: string) => void;
  onDelete: (name: string) => void;
}> = ({ item, onEdit, onDelete }) => {
  const { t } = useTranslation("common");
  const displayValue = resolveCriteriaDisplayValue(item.field, item.condition, item.value, item.displayValue, t, item.dynamicDate);

  return (
  <div
    className="flex items-center px-3 py-2 text-sm border-b border-gray-100 last:border-b-0
                hover:bg-blue-50 transition-colors duration-150 ease-in-out group"
  >
    <div
      className="flex flex-1 cursor-pointer truncate"
      onClick={() => {
        onEdit(item.field.name);
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit(item.field.name);
        }
      }}
    >
      <span className="mr-2 font-semibold text-gray-900">&quot;{item.field.label}&quot;</span>
      <span className="mr-2 italic text-gray-600">{item.condition.label}</span>
      <span className="text-gray-800">
        {item.field.type === "string" ? '"' : ""}
        {displayValue}
        {item.field.type === "string" ? '"' : ""}
      </span>
    </div>
    <button
      className="flex items-center justify-center h-8 w-8 text-gray-500 hover:text-red-600 hover:bg-red-50
               rounded-lg transition-all duration-200 ease-in-out
               focus:outline-hidden focus:ring-2 focus:ring-red-500/20
               opacity-0 group-hover:opacity-100"
      onClick={() => {
        onDelete(item.field.name);
      }}
      title="Supprimer ce critère"
      aria-label="Supprimer ce critère"
    >
      <FaSearchMinus className="h-4 w-4" />
    </button>
  </div>
  );
};

// Composant principal pour la gestion des critères de recherche
const U2000BasicSearchCriteria = forwardRef<U2000BasicSearchCriteriaHandle, U2000BasicSearchCriteriaProps>(
  ({ searchStateHandlers, filterKey, conditionTypes }, ref) => {
  const criteriaLineRef = useRef<U2000BasicSearchFieldCriteriaLineHandle>(null);
  const { i18n } = useTranslation("common");

  useImperativeHandle(
    ref,
    () => ({
      flushDraftCriteriaOnSearch(): U2000DraftCriteriaFlushResult {
        return criteriaLineRef.current?.flushDraftCriteriaOnSearch() ?? "noop";
      },
    }),
    [],
  );

  // Critère actuellement sélectionné pour édition (null si aucun)
  const [selectedItem, setSelectedItem] = useState<U2000CriteriaItem | null | undefined>(undefined);

  // Remet à zéro la sélection si le filtre change (utile lors d'un changement d'onglet ou de contexte)
  React.useEffect(() => {
    setSelectedItem(null);
  }, [filterKey]);

  // BL-0404 : critères persistés avec opérateur lookup retiré → equals (chips + état)
  React.useEffect(() => {
    const list = searchStateHandlers.getCriterias() ?? [];
    if (list.length === 0) return;
    let changed = false;
    const next = list.map((c) => {
      const coerced = u2000CoerceLookupCriteriaCondition(c.field, c.condition, conditionTypes, i18n.language);
      if (coerced.name !== c.condition.name) {
        changed = true;
        return { ...c, condition: coerced };
      }
      return c;
    });
    if (changed) {
      searchStateHandlers.setCriterias(next);
    }
  }, [conditionTypes, i18n.language, filterKey, searchStateHandlers]);

  // Permet de forcer le re-render du formulaire d'édition (clé unique)
  const [formSessionId, setFormSessionId] = useState(0);

  // Toujours lire le reducer : `useMemo([getCriterias()])` figeait une référence / un snapshot incorrect (liste « Critères actifs » ≠ extractSearchParameter).
  const criteriaList = searchStateHandlers.getCriterias() ?? [];

  // Edit the current criteria from the list of criterias
  // Passe en mode édition pour le critère sélectionné (ou annule si non trouvé)
  const onEdit = React.useCallback(
    (name: string) => {
      const criteriaToEdit = criteriaList.find((criteria: U2000CriteriaItem) => criteria.field.name === name);
      setFormSessionId((id) => id + 1);
      if (criteriaToEdit) {
        setSelectedItem({ ...criteriaToEdit, timestamps: Date.now() });
      } else {
        setSelectedItem(null);
      }
    },
    [criteriaList]
  );

  /**
   * Ajoute un nouveau critère ou met à jour un critère existant.
   * Si le champ existe déjà, il est mis à jour, sinon un nouveau critère est ajouté avec un id unique.
   */
  function onAddOrUpdate(field: U2000TableColumn, condition: U2000Condition, value: string | null, displayValue?: string) {
    searchStateHandlers.addOrUpdateCriterias(field, condition, value || "", displayValue);
    setSelectedItem(null);
    setFormSessionId((id) => id + 1);
  }

  // Annule l'édition en cours et force le re-render du formulaire
  function onCancelEdit() {
    setSelectedItem(null);
    setFormSessionId((id) => id + 1);
  }

  // Delete the current criteria from the list of criterias
  // Supprime le critère correspondant au nom de champ donné
  const onDelete = React.useCallback(
    (name: string) => {
      searchStateHandlers.deleteCriterias(name);
    },
    [searchStateHandlers]
  );

  return (
    <div>
      {/* Formulaire d'édition/ajout de critère */}
      <U2000BasicSearchFieldCriteriaLine
        ref={criteriaLineRef}
        key={`criteria-form-${formSessionId}-${filterKey ?? ""}`}
        columns={searchStateHandlers.getColumns()}
        conditionTypes={conditionTypes}
        onAddCondition={onAddOrUpdate}
        selectedItem={selectedItem}
        onCancelEdit={onCancelEdit}
      />

      {/* Liste des critères ajoutés, chaque ligne est éditable ou supprimable */}
      <div className="mt-3 rounded-lg border border-gray-300 bg-white shadow-xs overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
          <p className="text-sm font-medium text-gray-900">Critères actifs</p>
        </div>
        <div className="h-[120px] overflow-auto">
          {criteriaList.length > 0 ? (
            criteriaList.map((criteria: U2000CriteriaItem) => <CriteriaLine key={criteria.id} item={criteria} onEdit={onEdit} onDelete={onDelete} />)
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-500 italic">Aucun critère défini</div>
          )}
        </div>
      </div>
    </div>
  );
});

U2000BasicSearchCriteria.displayName = "U2000BasicSearchCriteria";

export default U2000BasicSearchCriteria;
